// Tenant-staff hooks for /api/v1/auditor-tokens
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';
import type { AuditorTokenSummary, AuditorTokenStatus } from '@grc/shared';

const KEYS = {
  list: (tenantId: string | null, filters?: object) => ['auditor', 'tokens', tenantId, filters],
  detail: (tenantId: string | null, id: string) => ['auditor', 'tokens', tenantId, id],
};

export function useAuditorTokens(filters?: { status?: AuditorTokenStatus; framework_id?: string }) {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: KEYS.list(tenantId, filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status) params.set('status', filters.status);
      if (filters?.framework_id) params.set('framework_id', filters.framework_id);
      const { data } = await apiClient.get(`/auditor-tokens?${params.toString()}`);
      return data as { data: AuditorTokenSummary[]; meta: { total: number } };
    },
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

export function useAuditorTokenDetail(id: string) {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: KEYS.detail(tenantId, id),
    queryFn: async () => {
      const { data } = await apiClient.get(`/auditor-tokens/${id}`);
      return data.data as AuditorTokenSummary & {
        auditLogSlice: Array<{ event_type: string; occurred_at: string; metadata: object }>;
      };
    },
    enabled: !!tenantId && !!id,
  });
}

export function useCreateAuditorToken() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      auditor_email: string;
      auditor_display_name: string;
      audit_firm_name: string;
      framework_ids: string[];
      lifecycle_days: 7 | 14 | 30;
    }) => {
      const { data } = await apiClient.post('/auditor-tokens', body);
      return data.data as AuditorTokenSummary;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['auditor', 'tokens', tenantId] }),
  });
}

export function useRevokeAuditorToken() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const { data } = await apiClient.post(`/auditor-tokens/${id}/revoke`, { reason });
      return data.data as AuditorTokenSummary;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['auditor', 'tokens', tenantId] }),
  });
}

export function useResendAuditorInvitation() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post(`/auditor-tokens/${id}/resend-invitation`);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['auditor', 'tokens', tenantId] }),
  });
}
